import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { UserPlus, Send, Loader2, CheckCircle, XCircle, Clock, Euro, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = { pending: "#FFB800", accepted: "#00F0FF", rejected: "#FF3B30" };
const statusLabels = { pending: "Ausstehend", accepted: "Angenommen", rejected: "Abgelehnt" };

export default function ApplicationPanel({ projectId, projectOwnerId }) {
  const { user } = useAuth();
  const role = user?.role || "client";
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ message: "", proposed_rate: "" });
  const [submitting, setSubmitting] = useState(false);
  const [applied, setApplied] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const fetchApps = () => {
    axios.get(`${API}/projects/${projectId}/applications`, { withCredentials: true })
      .then(res => {
        setApplications(res.data);
        setApplied(res.data.some(a => a.freelancer_id === user?.user_id));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchApps(); }, [projectId]);

  const handleApply = async () => {
    if (!form.message.trim() || !form.proposed_rate) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/projects/${projectId}/apply`, {
        message: form.message, proposed_rate: parseFloat(form.proposed_rate),
      }, { withCredentials: true });
      setApplied(true);
      setShowForm(false);
      fetchApps();
    } catch (err) {
      console.error(err);
    }
    setSubmitting(false);
  };

  const handleRespond = async (appId, action) => {
    try {
      await axios.post(`${API}/projects/${projectId}/applications/${appId}/${action}`, {}, { withCredentials: true });
      fetchApps();
    } catch (err) { console.error(err); }
  };

  const pendingCount = applications.filter(a => a.status === "pending").length;

  return (
    <div data-testid="application-panel" className="glass-surface rounded-xl overflow-hidden">
      <button onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors">
        <div className="flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-[#00F0FF]" />
          <h3 className="font-['Outfit'] text-sm font-semibold text-white">Bewerbungen</h3>
          {pendingCount > 0 && (
            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#FFB800] text-black text-[10px] font-bold">{pendingCount}</span>
          )}
          <span className="text-[10px] font-['JetBrains_Mono'] text-[#606070]">{applications.length} gesamt</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#606070]" /> : <ChevronDown className="w-4 h-4 text-[#606070]" />}
      </button>

      {expanded && (
        <div className="border-t border-white/5">
          {/* Freelancer Apply Button */}
          {role === "freelancer" && !applied && (
            <div className="px-5 py-4 border-b border-white/5">
              {!showForm ? (
                <button data-testid="apply-btn" onClick={() => setShowForm(true)}
                  className="btn-primary w-full flex items-center justify-center gap-2 text-sm">
                  <UserPlus className="w-4 h-4" /> Auf dieses Projekt bewerben
                </button>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-[#A0A0B0] mb-1 block">Dein Stundensatz (EUR) *</label>
                    <input data-testid="apply-rate" type="number" min="1" value={form.proposed_rate}
                      onChange={e => setForm(p => ({ ...p, proposed_rate: e.target.value }))}
                      placeholder="z.B. 85"
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] px-3 py-2 text-sm outline-none rounded-lg font-['JetBrains_Mono']" />
                  </div>
                  <div>
                    <label className="text-xs text-[#A0A0B0] mb-1 block">Nachricht an den Auftraggeber *</label>
                    <textarea data-testid="apply-message" rows={3} value={form.message}
                      onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                      placeholder="Warum bist du der richtige Auftragnehmer fuer dieses Projekt?"
                      className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] px-3 py-2 text-sm outline-none resize-none rounded-lg" />
                  </div>
                  <div className="flex gap-2">
                    <button data-testid="apply-submit" onClick={handleApply} disabled={submitting}
                      className="btn-primary flex items-center gap-2 text-sm flex-1 justify-center">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Bewerbung senden
                    </button>
                    <button onClick={() => setShowForm(false)} className="btn-secondary text-sm px-4">Abbrechen</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {role === "freelancer" && applied && (
            <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2 bg-[#00F0FF]/5">
              <CheckCircle className="w-4 h-4 text-[#00F0FF]" />
              <span className="text-xs text-[#00F0FF] font-['Outfit']">Du hast dich bereits beworben</span>
            </div>
          )}

          {/* Applications List */}
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 text-[#00F0FF] animate-spin" /></div>
          ) : applications.length === 0 ? (
            <div className="px-5 py-6 text-center">
              <UserPlus className="w-8 h-8 text-[#606070] mx-auto mb-2" />
              <p className="text-xs text-[#606070]">Noch keine Bewerbungen</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {applications.map(app => (
                <div key={app.id} className="px-5 py-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#00F0FF]/10 flex items-center justify-center text-xs font-bold text-[#00F0FF]">
                        {(app.freelancer_name || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <span className="text-sm text-white font-medium block">{app.freelancer_name}</span>
                        <span className="text-[10px] text-[#606070] font-['JetBrains_Mono']">
                          {new Date(app.created_at).toLocaleDateString("de-DE")}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-['JetBrains_Mono'] font-bold text-white flex items-center gap-1">
                        <Euro className="w-3 h-3 text-[#A0A0B0]" />{app.proposed_rate}/h
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-['JetBrains_Mono']"
                        style={{ color: statusColors[app.status], background: `${statusColors[app.status]}15` }}>
                        {statusLabels[app.status]}
                      </span>
                    </div>
                  </div>

                  <div className="ml-10 mb-2 px-3 py-2 bg-white/[0.02] border border-white/5 rounded-lg">
                    <MessageSquare className="w-3 h-3 text-[#606070] inline mr-1.5" />
                    <span className="text-xs text-[#A0A0B0]">{app.message}</span>
                  </div>

                  {/* Accept/Reject buttons for project owner */}
                  {role === "client" && app.status === "pending" && (
                    <div className="ml-10 flex gap-2">
                      <button data-testid={`accept-app-${app.id}`} onClick={() => handleRespond(app.id, "accept")}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00F0FF]/10 border border-[#00F0FF]/20 text-[#00F0FF] text-xs rounded-lg hover:bg-[#00F0FF]/20 transition-colors">
                        <CheckCircle className="w-3 h-3" /> Annehmen
                      </button>
                      <button data-testid={`reject-app-${app.id}`} onClick={() => handleRespond(app.id, "reject")}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF3B30]/10 border border-[#FF3B30]/20 text-[#FF3B30] text-xs rounded-lg hover:bg-[#FF3B30]/20 transition-colors">
                        <XCircle className="w-3 h-3" /> Ablehnen
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
